import type { TankDTO } from "../../../shared/protocol/index.js";
import { state } from "../../state.js";
import type { Renderer } from "../Renderer.js";
import { drawWindupStubs } from "../scope.js";
import { RECOIL_MS, SPAWN_ANIM_MS } from "../types.js";
import { roundRect, shade, tankTreadPhase } from "../util.js";
import { drawBattleCityTank } from "./battleCity.js";
import { drawLocalIndicator } from "./indicator.js";
import { drawModernTank } from "./modern.js";
import { drawRealisticTank } from "./realistic.js";

/** Draw one tank in the active visual theme (plus its status extras + label). */
export function drawTank(rd: Renderer, t: TankDTO, isLocal: boolean, nowMs: number): void {
  const { ctx } = rd;
  ctx.save();
  ctx.translate(t.x, t.y);
  ctx.globalAlpha = t.alive ? 1 : 0.25;

  // Transient transforms: respawn pop-in scale + recoil kick. Both apply to
  // the hull/turret only — labels and health bars draw after the restore.
  const f = rd.tankFx.get(t.id);
  if (f && t.alive) {
    const sAge = (nowMs - f.spawnAt) / SPAWN_ANIM_MS;
    if (f.spawnAt > 0 && sAge >= 0 && sAge < 1) {
      // Overshoot ease: grows from 60% past 100% and settles.
      const p = sAge - 1;
      const k = 0.6 + 0.4 * (1 + 2.2 * p * p * p + 1.2 * p * p);
      ctx.scale(k, k);
    }
    const rAge = (nowMs - f.recoilAt) / RECOIL_MS;
    if (f.recoilAt > 0 && rAge >= 0 && rAge < 1) {
      const push = 2.2 * (1 - rAge);
      ctx.translate(-Math.cos(t.turretAngle) * push, -Math.sin(t.turretAngle) * push);
    }
  }

  const r = rd.tankR;
  // Signed distance rolled along the tank's own forward axis since last frame
  // — drives the tread/wheel scroll below in lock-step with real motion
  // (forward scrolls one way, reverse the other, no motion = frozen).
  const treadPhase = tankTreadPhase(rd, t);

  if (state.realisticEnabled) {
    drawRealisticTank(rd, t, isLocal, nowMs);
    return;
  }

  if (state.modernEnabled) {
    drawModernTank(rd, t, isLocal, nowMs);
    return;
  }

  if (state.battleCityEnabled) {
    drawBattleCityTank(rd, t, isLocal, nowMs, treadPhase);
    return;
  }

  // Default Tanka Maze vector tank drawing code:
  // Local-player facing indicator: a glowing sky-blue arrow ahead of your tank
  // so you can find yourself and read your heading at a glance.
  if (isLocal && t.alive) {
    drawLocalIndicator(ctx, r, t.bodyAngle, nowMs);
  }

  // Body (oriented to movement direction).
  ctx.save();
  ctx.rotate(t.bodyAngle);
  // Elongated hull: longer front-to-back (x) than it is wide (y).
  const halfLen = r * 1.3;
  const halfWid = r * 0.82;

  // Treads: two fixed belt bars (they stay put) with cleat lines that scroll
  // across them by distance traveled — clipped to the belt so the lugs roll
  // off the ends instead of the whole strip sliding (no sawtooth march).
  const beltH = 4;
  const beltX = -halfLen + 2;
  const beltW = halfLen * 2 - 4;
  const treadPitch = 5;
  const treadShift = ((treadPhase % treadPitch) + treadPitch) % treadPitch;
  for (const beltY of [-halfWid - beltH, halfWid]) {
    // Fixed belt bar.
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(beltX, beltY, beltW, beltH);
    // Scrolling cleats, clipped to the belt so they never spill past its ends.
    ctx.save();
    ctx.beginPath();
    ctx.rect(beltX, beltY, beltW, beltH);
    ctx.clip();
    ctx.strokeStyle = "rgba(255,252,240,0.45)";
    ctx.lineWidth = 1.5;
    for (let lx = beltX - treadPitch + treadShift; lx < beltX + beltW + treadPitch; lx += treadPitch) {
      ctx.beginPath();
      ctx.moveTo(lx, beltY);
      ctx.lineTo(lx, beltY + beltH);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Hull: rounded rectangle chassis with an outline for definition.
  ctx.fillStyle = t.color;
  roundRect(ctx, -halfLen, -halfWid, halfLen * 2, halfWid * 2, 6);
  ctx.fill();

  // Sketched outlines (double outlines with slight offset for hand-drawn look)
  ctx.strokeStyle = "rgba(0,0,0,0.7)";
  ctx.lineWidth = 1.4;
  roundRect(ctx, -halfLen, -halfWid, halfLen * 2, halfWid * 2, 6);
  ctx.stroke();

  ctx.strokeStyle = "rgba(0,0,0,0.3)";
  ctx.lineWidth = 0.8;
  roundRect(ctx, -halfLen + 0.5, -halfWid + 0.5, halfLen * 2 - 1, halfWid * 2 - 1, 6);
  ctx.stroke();

  // Rear engine deck vents (sketched parallel lines)
  ctx.strokeStyle = "rgba(0,0,0,0.45)";
  ctx.lineWidth = 1.0;
  for (let vx = -halfLen + 4; vx < -halfLen * 0.3; vx += 4) {
    ctx.beginPath();
    ctx.moveTo(vx, -halfWid * 0.65);
    ctx.lineTo(vx, halfWid * 0.65);
    ctx.stroke();
  }

  // Front plate: a brighter band on the leading (+x) edge marks the front
  ctx.fillStyle = shade(t.color, 45);
  roundRect(ctx, halfLen * 0.4, -halfWid * 0.8, halfLen * 0.55, halfWid * 1.6, 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.65)";
  ctx.lineWidth = 1.2;
  roundRect(ctx, halfLen * 0.4, -halfWid * 0.8, halfLen * 0.55, halfWid * 1.6, 2);
  ctx.stroke();

  // Sketched rivets on front plate corners
  ctx.fillStyle = "rgba(0,0,0,0.7)";
  const rivets = [
    { x: halfLen * 0.46, y: -halfWid * 0.6 },
    { x: halfLen * 0.46, y: halfWid * 0.6 },
    { x: halfLen * 0.86, y: -halfWid * 0.6 },
    { x: halfLen * 0.86, y: halfWid * 0.6 }
  ];
  for (const rPt of rivets) {
    ctx.beginPath();
    ctx.arc(rPt.x, rPt.y, 1, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // Turret + barrel (aim direction).
  ctx.save();
  ctx.rotate(t.turretAngle);

  // Barrel: sketched look
  ctx.fillStyle = "rgba(0,0,0,0.65)";
  ctx.fillRect(0, -3.2, r + 10, 6.4);
  ctx.fillStyle = shade(t.color, -10);
  ctx.fillRect(0, -2, r + 9, 4);

  // Muzzle brake tip detail
  ctx.fillStyle = "rgba(0,0,0,0.8)";
  ctx.fillRect(r + 8, -4.5, 3, 9);
  ctx.fillStyle = shade(t.color, -30);
  ctx.fillRect(r + 9, -3.5, 1.5, 7);

  // Turret dome
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.6, 0, Math.PI * 2);
  ctx.fillStyle = shade(t.color, -20);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.75)";
  ctx.lineWidth = 1.6;
  ctx.stroke();

  // Double sketched turret dome outline
  ctx.beginPath();
  ctx.arc(0.5, 0.5, r * 0.58, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(0,0,0,0.3)";
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // Hatch outline
  ctx.beginPath();
  ctx.arc(-r * 0.15, 0, r * 0.22, 0, Math.PI * 2);
  ctx.fillStyle = shade(t.color, -35);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.6)";
  ctx.lineWidth = 1.0;
  ctx.stroke();

  // Hatch hinge detail
  ctx.beginPath();
  ctx.moveTo(-r * 0.25, -r * 0.1);
  ctx.lineTo(-r * 0.25, r * 0.1);
  ctx.stroke();

  ctx.restore();

  // Laser windup preview: a thin flickering beam along the aim.
  if (t.charging && t.alive) {
    ctx.save();
    ctx.rotate(t.turretAngle);
    ctx.globalAlpha = 0.4 + 0.35 * Math.abs(Math.sin(nowMs / 60));
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.5;
    drawWindupStubs(rd, ctx, r, t.weaponCharges);
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  // Shield bubble.
  if (t.shielded && t.alive) {
    ctx.beginPath();
    ctx.arc(0, 0, r + 8, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(79,214,160,0.18)";
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = `rgba(79,214,160,${0.55 + 0.3 * Math.abs(Math.sin(nowMs / 200))})`;
    ctx.stroke();
  }

  ctx.globalAlpha = 1;
  ctx.restore();

  // Health bar (only when HP > 1).
  if (t.alive && t.maxHp > 1) {
    const bw = rd.tankR * 2;
    const bx = t.x - rd.tankR;
    const by = t.y - rd.tankR - 6;
    const frac = Math.max(0, Math.min(1, t.hp / t.maxHp));
    ctx.fillStyle = "rgba(47,42,34,0.35)";
    ctx.fillRect(bx, by, bw, 3);
    ctx.fillStyle = frac > 0.5 ? "#4d7a40" : frac > 0.25 ? "#e6863f" : "#b23b2e";
    ctx.fillRect(bx, by, bw * frac, 3);
  }

  // Name / respawn label (unrotated).
  ctx.font = "bold 11px system-ui, sans-serif";
  ctx.textAlign = "center";
  if (t.alive) {
    ctx.fillStyle = "rgba(13,17,23,0.9)";
    // Normally above the tank; flip below when it'd clip off the canvas top.
    const above = t.y - rd.tankR - (t.maxHp > 1 ? 13 : 8);
    const labelY = above < 10 ? t.y + rd.tankR + 14 : above;
    ctx.fillText(t.name, t.x, labelY);
  } else {
    ctx.fillStyle = "rgba(13,17,23,0.65)";
    ctx.fillText(`${Math.ceil(t.respawnIn)}`, t.x, t.y + 4);
  }
}
